import { memo, useState, useEffect, useCallback, KeyboardEvent } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";

import deleteSvg from "assets/svg/delete.svg";

import { MultipleChartBlockProps, MultipleChartColumnData } from "types/Charts";
import { CorrelationChartColumn } from "./CorrelationChartColumn";
import Avatar from "@mui/material/Avatar";

export const CorrelationChartBlock: React.FC<MultipleChartBlockProps> = memo(
    (props) => {
        const {
            blockKey,
            onDelete,
            onChange,
            defaultValue,
            isSingleUpdate,
            isEdit,
            onSubmitMultipleChart,
        } = props;

        const [formValues, setFormValues] = useState<MultipleChartColumnData>({
            multipleChartType: "different",
            chart_name: "Compare Sensor with Actuator",
            data: {},
        });

        useEffect(() => {
            defaultValue && setFormValues(defaultValue);
        }, [defaultValue]);

        /**
         * This update will trigger the onChange event even at the initial render (users not change anything yet)
         * Ensure that the parent component always have the same formValues format when users submit without any changes
         */
        useEffect(() => {
            onChange(formValues);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formValues]);

        const handleSensorColumnChange = useCallback((data: any) => {
            setFormValues((prev: any) => ({
                ...prev,
                data: {
                    ...prev.data,
                    0: data,
                },
            }));
        }, []);

        const handleActuatorColumnChange = useCallback((data: any) => {
            setFormValues((prev: any) => ({
                ...prev,
                data: {
                    ...prev.data,
                    1: data,
                },
            }));
        }, []);

        const selectedSensorCount = Object.keys(
            formValues.data?.[0]?.devices ?? {}
        ).length;
        const selectedActuatorCount = Object.keys(
            formValues.data?.[1]?.devices ?? {}
        ).length;
        const showMinCorrelationMessage =
            selectedSensorCount === 0 || selectedActuatorCount === 0;

        return (
            <div className="multiple-chart-block rounded p-4">
                <Row>
                    <Col xs={12}>
                        <Row>
                            <Col sm={12} className="mb-3">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                        <p>Widget name</p>
                                        <Form.Control
                                            type="text"
                                            className="multiple-chart-name"
                                            placeholder="Enter widget name"
                                            value={formValues.chart_name || ""}
                                            onChange={(event) => {
                                                setFormValues({
                                                    ...formValues,
                                                    chart_name:
                                                        event.target.value,
                                                });
                                            }}
                                            onKeyPress={(
                                                e: KeyboardEvent<HTMLInputElement>
                                            ) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    if (onSubmitMultipleChart)
                                                        onSubmitMultipleChart(
                                                            e
                                                        );
                                                }
                                            }}
                                        />
                                    </div>
                                    {!isSingleUpdate && (
                                        <Avatar
                                            alt="delete"
                                            src={deleteSvg}
                                            onClick={() => onDelete?.(blockKey)}
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                cursor: "pointer",
                                            }}
                                        />
                                    )}
                                </div>
                            </Col>

                            <Col sm={12}>
                                <Row>
                                    <CorrelationChartColumn
                                        key="sensor-column"
                                        isEdit={isEdit}
                                        column={0}
                                        data={formValues}
                                        columnType="sensor"
                                        onChange={handleSensorColumnChange}
                                    />

                                    <CorrelationChartColumn
                                        key="actuator-column"
                                        isEdit={isEdit}
                                        column={1}
                                        data={formValues}
                                        columnType="actuator"
                                        onChange={handleActuatorColumnChange}
                                    />
                                </Row>
                            </Col>

                            {showMinCorrelationMessage && (
                                <Col sm={12} className="mt-3 text-danger">
                                    A minimum of one sensor and one actuator
                                    must be selected.
                                </Col>
                            )}
                        </Row>
                    </Col>
                </Row>
            </div>
        );
    }
);
